import { useState } from "react"

import { Button } from "@coval/ui"
import { RotateCcw, Tags } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { MetadataFilterEditor } from "@/components/filters/MetadataFilterEditor"

import { useConversationsFilter } from "../../contexts/ConversationsFilterContext"
import { useMetadataSuggestionsTransport } from "../../routes/useMetadataSuggestionsTransport"
import type { ConversationsFilterSectionProps } from "../../types/filters"

export function MetadataFilterSection({
  isOpen,
  onToggle,
  filterEnabled = true,
}: ConversationsFilterSectionProps) {
  const {
    state: { metadataFilters },
    handlerAddMetadataFilter,
    handlerSetMetadataFilters,
  } = useConversationsFilter()
  const [key, setKey] = useState("")
  const [value, setValue] = useState("")
  const suggestions = useMetadataSuggestionsTransport(
    key,
    value,
    isOpen && filterEnabled,
  )

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      width="w-96"
      trigger={
        <FilterTrigger
          label="Metadata"
          icon={<Tags className="h-4 w-4" />}
          isActive={metadataFilters.length > 0}
        />
      }
    >
      <div className="max-h-[50vh] overflow-y-auto">
        <MetadataFilterEditor
          filters={metadataFilters}
          onAddFilter={(filter) =>
            filter.matchType === "ANY_OF"
              ? handlerAddMetadataFilter(
                  filter.key,
                  filter.value,
                  filter.matchType,
                  filter.values,
                )
              : handlerAddMetadataFilter(
                  filter.key,
                  filter.value,
                  filter.matchType,
                )
          }
          onChangeFilters={handlerSetMetadataFilters}
          keyOptions={suggestions.keys}
          valueOptions={suggestions.values}
          onKeyChange={setKey}
          onValueChange={setValue}
          addLabel="Add"
          suggestionsStatus={
            <>
              {(suggestions.isLoading || !filterEnabled) && (
                <p role="status" className="text-xs text-coval-text-muted">
                  Loading suggestions...
                </p>
              )}
              {suggestions.isError && (
                <div className="flex items-center justify-between">
                  <p role="status" className="text-xs text-coval-text-muted">
                    Suggestions unavailable. You can still type a key and value.
                  </p>
                  <Button
                    variant="ghost"
                    onClick={suggestions.retry}
                    aria-label="Retry loading suggestions"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Retry
                  </Button>
                </div>
              )}
            </>
          }
        />
      </div>
    </FilterPopover>
  )
}
